import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** @type {IntersectionObserver | null} */
  #loadObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let sourcesAttached = false;

    const tryAutoplay = () => {
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    const attachSources = () => {
      if (sourcesAttached) return;
      sourcesAttached = true;

      for (const source of videoSources) {
        const { videoSource } = source.dataset;

        if (videoSource) source.setAttribute('src', videoSource);
      }

      // Background videos get their sources assigned after mount, so explicitly
      // re-assert autoplay-critical flags and trigger play for Safari/webkit.
      videoElement.muted = true;
      videoElement.defaultMuted = true;
      videoElement.playsInline = true;
      videoElement.autoplay = true;

      videoElement.addEventListener('loadeddata', tryAutoplay, { once: true });
      videoElement.load();
      tryAutoplay();
    };

    if (!reduceMotion) {
      this.#loadObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          attachSources();
          this.#loadObserver?.disconnect();
          this.#loadObserver = null;
        },
        { rootMargin: '100% 0px' }
      );
      this.#loadObserver.observe(this);
    }

    this.#visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting) {
          if (sourcesAttached) tryAutoplay();
        } else {
          videoElement.pause();
        }
      },
      {
        threshold: 0.5,
      }
    );

    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#loadObserver?.disconnect();
    this.#loadObserver = null;
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
